import React, { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Search, QrCode, Sparkles, ChevronLeft, ChevronRight, Paintbrush, HardHat, Coins, IndianRupee, Hash, Check } from "lucide-react";

const STEPS = ["Audience", "Reward Points", "Value (₹)", "Quantity"];

export default function NewBatchModal({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [targetRole, setTargetRole] = useState("");
  const [rewardPoints, setRewardPoints] = useState("");
  const [rewardRs, setRewardRs] = useState("");
  const [quantity, setQuantity] = useState(50);
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (step !== 3) return;
    const id = setTimeout(() => {
      api.get("/products", { params: { search: q, page_size: 8 } })
        .then((r) => setProducts(r.data.items || []))
        .catch(() => setProducts([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q, step]);

  const canNext = () => {
    if (step === 0) return !!targetRole;
    if (step === 1) return rewardPoints !== "" && Number(rewardPoints) >= 0;
    if (step === 2) return rewardRs !== "" && Number(rewardRs) >= 0;
    return true;
  };

  const next = () => { if (canNext()) setStep((s) => Math.min(s + 1, 3)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const create = async () => {
    if (!targetRole) { toast.error("Select an audience"); setStep(0); return; }
    if (quantity < 1 || quantity > 5000) { toast.error("Quantity must be between 1 and 5000"); return; }
    setBusy(true);
    try {
      const roleLabel = targetRole.charAt(0).toUpperCase() + targetRole.slice(1);
      const body = {
        target_role: targetRole,
        reward_points: Number(rewardPoints),
        reward_value_rs: Number(rewardRs),
        quantity: Number(quantity),
        product_id: product ? product.id : undefined,
        name: name || (product ? `Batch ${product.name}` : `${roleLabel} QR Batch`),
        expiry: expiry ? new Date(expiry).toISOString() : null,
      };
      const { data } = await api.post("/coupon-batches", body);
      toast.success(`Generated ${data.generated} QR coupons for ${roleLabel}s`);
      onCreated(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const RoleBtn = ({ value, icon: Icon, label }) => (
    <button data-testid={`batch-role-${value}`} onClick={() => setTargetRole(value)}
      className={`flex-1 flex flex-col items-center gap-3 rounded-2xl border-2 px-4 py-8 transition-all ${targetRole === value ? "border-[#8B0000] bg-[#FDF2F2] shadow-sm" : "border-gray-200 hover:border-gray-300"}`}>
      <span className={`h-14 w-14 rounded-full flex items-center justify-center ${targetRole === value ? "bg-[#8B0000] text-white" : "bg-gray-100 text-gray-500"}`}><Icon className="h-7 w-7" /></span>
      <span className={`font-bold ${targetRole === value ? "text-[#8B0000]" : "text-gray-700"}`}>{label}</span>
      {targetRole === value && <Check className="h-4 w-4 text-[#8B0000]" />}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="new-batch-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[#FDF2F2] flex items-center justify-center text-[#8B0000]"><QrCode className="h-4 w-4" /></div>
            <h3 className="font-display font-bold text-gray-900">Generate New QR Batch</h3>
          </div>
          <button data-testid="new-batch-close" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        {/* stepper */}
        <div className="flex items-center gap-1 px-5 pt-4">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1 flex items-center gap-1">
              <div className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#8B0000]" : "bg-gray-200"}`} />
            </div>
          ))}
        </div>
        <div className="px-5 pt-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Step {step + 1} of 4 · {STEPS[step]}</div>

        <div className="p-5 min-h-[220px]">
          {step === 0 && (
            <div>
              <label className="text-sm font-semibold text-gray-700">Generate QR codes for whom?</label>
              <div className="mt-3 flex gap-3">
                <RoleBtn value="painter" icon={Paintbrush} label="Painter" />
                <RoleBtn value="contractor" icon={HardHat} label="Contractor" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Coins className="h-4 w-4 text-[#8B0000]" /> Reward points per QR code</label>
              <p className="text-xs text-gray-500 mt-1">Points credited to the {targetRole}'s wallet when they scan.</p>
              <input data-testid="batch-reward-points" type="number" min="0" autoFocus value={rewardPoints} onChange={(e) => setRewardPoints(e.target.value)}
                placeholder="e.g. 50"
                className="mt-4 w-full border border-gray-300 rounded-lg px-4 py-3 text-lg font-bold outline-none focus:border-[#8B0000]" />
            </div>
          )}

          {step === 2 && (
            <div>
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-2"><IndianRupee className="h-4 w-4 text-[#8B0000]" /> Value of each QR code (₹)</label>
              <p className="text-xs text-gray-500 mt-1">Rupee value represented by a single QR code.</p>
              <input data-testid="batch-reward-rs" type="number" min="0" step="0.01" autoFocus value={rewardRs} onChange={(e) => setRewardRs(e.target.value)}
                placeholder="e.g. 50"
                className="mt-4 w-full border border-gray-300 rounded-lg px-4 py-3 text-lg font-bold outline-none focus:border-[#8B0000]" />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Hash className="h-4 w-4 text-[#8B0000]" /> Quantity of QR codes</label>
                <input data-testid="batch-quantity" type="number" min="1" max="5000" autoFocus value={quantity} onChange={(e) => setQuantity(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#8B0000]" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Link a product <span className="font-normal text-gray-400">(optional)</span></label>
                {product ? (
                  <div className="mt-1 flex items-center justify-between border-2 border-[#8B0000] bg-[#FDF2F2] rounded-lg px-3 py-2.5">
                    <div><div className="font-semibold text-sm text-gray-900">{product.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{product.code}</div></div>
                    <button data-testid="batch-change-product" onClick={() => setProduct(null)} className="text-xs font-semibold text-[#8B0000]">Remove</button>
                  </div>
                ) : (
                  <>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <input data-testid="batch-product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product by name, code or token name"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#8B0000]" />
                    </div>
                    {q && (
                      <div className="mt-2 max-h-36 overflow-y-auto scrollbar-thin border border-gray-100 rounded-lg divide-y">
                        {products.map((p) => (
                          <button key={p.id} data-testid={`batch-product-${p.id}`} onClick={() => { setProduct(p); if (!name) setName(`Batch ${p.name}`); }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50">
                            <div className="text-sm font-medium text-gray-900">{p.name}</div>
                            <div className="text-xs text-gray-500 font-mono">{p.code}{p.token_name ? <span className="ml-2 font-sans font-semibold text-[#8B0000]">Token: {p.token_name}</span> : null}</div>
                          </button>
                        ))}
                        {products.length === 0 && <div className="px-3 py-4 text-sm text-gray-400 text-center">No products found</div>}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-semibold text-gray-700">Batch Name <span className="font-normal text-gray-400">(optional)</span></label>
                  <input data-testid="batch-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Auto"
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#8B0000]" /></div>
                <div><label className="text-sm font-semibold text-gray-700">Expiry <span className="font-normal text-gray-400">(optional)</span></label>
                  <input data-testid="batch-expiry" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#8B0000]" /></div>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
                <span className="font-semibold text-gray-900 capitalize">{targetRole}</span> · <span className="font-semibold">{rewardPoints || 0} pts</span> · <span className="font-semibold">₹{rewardRs || 0}</span> per QR × <span className="font-semibold">{quantity}</span> codes
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-between gap-2">
          <button onClick={step === 0 ? onClose : back} data-testid="batch-back-btn"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-semibold">
            {step === 0 ? "Cancel" : <><ChevronLeft className="h-4 w-4" /> Back</>}
          </button>
          {step < 3 ? (
            <button data-testid="batch-next-btn" onClick={next} disabled={!canNext()}
              className="flex items-center gap-1.5 bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-5 py-2.5 rounded-lg disabled:opacity-40">
              Next <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button data-testid="batch-generate-btn" onClick={create} disabled={busy}
              className="flex items-center gap-2 bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-5 py-2.5 rounded-lg disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate QR Codes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
